import {
  Box,
  Button,
  TagColor as Color,
  List,
  SearchField,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { DataSchemaItemFragment } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { ReducerState } from '../editableContentReducer'
import { FilterStatuses } from './Filters'

interface FieldErrors {
  count: number
  field: DataSchemaItemFragment | undefined
  filter?: FilterStatuses
}

const FieldListItem = ({
  fieldErrors,
  setUnrecognizedEnumName,
  setRowFilter,
  onClose,
}: {
  fieldErrors: FieldErrors
  setUnrecognizedEnumName: (fieldId: string) => void
  setRowFilter: (status: FilterStatuses) => void
  onClose: () => void
}) => {
  const label =
    fieldErrors.field?.fieldType === 'enum'
      ? i18n.t('importer_process.edition_step.sidepanel.field_replace')
      : i18n.t('importer_process.edition_step.sidepanel.field_review')

  const action = () => {
    if (fieldErrors.field?.fieldType === 'enum') {
      setUnrecognizedEnumName(fieldErrors.field.fieldName)
      return
    }

    if (fieldErrors.filter) {
      setRowFilter(fieldErrors.filter)
      onClose()
      return
    }
  }
  const actionIcon = fieldErrors.field?.fieldType === 'enum' ? 'search' : 'refresh'
  const textColor = fieldErrors.field?.required ? 'radicalRed1200' : 'grey1000'
  const tagColor = fieldErrors.field?.required ? Color.radical : Color.neutral

  return (
    <List.Item key={fieldErrors.field?.fieldName}>
      <List.Title>
        <Box flexDirection="row" gap="s12">
          <Tag type={tagColor}>{fieldErrors.count}</Tag>
          <Text.Normal color={textColor} weight="medium">
            {fieldErrors.field?.label}
          </Text.Normal>
        </Box>
      </List.Title>
      <List.Description>
        <Button iconName={actionIcon} onClick={action} type="secondary">
          {label}
        </Button>
      </List.Description>
    </List.Item>
  )
}

const FieldList = ({
  fieldsErrors,
  setUnrecognizedEnumName,
  setRowFilter,
  onClose,
}: {
  fieldsErrors: { count: number; field: DataSchemaItemFragment | undefined }[]
  setUnrecognizedEnumName: (fieldId: string) => void
  setRowFilter: (status: FilterStatuses) => void
  onClose: () => void
}) => {
  return (
    <List>
      {fieldsErrors
        .filter((error) => error.count > 0)
        .map((error) => (
          <FieldListItem
            key={error.field?.fieldName}
            fieldErrors={error}
            setUnrecognizedEnumName={setUnrecognizedEnumName}
            setRowFilter={setRowFilter}
            onClose={onClose}
          />
        ))}
    </List>
  )
}

export interface ErrorsSidepanelProps {
  fields: DataSchemaItemFragment[]
  onClose: () => void
  stepData: ReducerState
  setUnrecognizedEnumName: (fieldId: string) => void
  setRowFilter: (status: FilterStatuses) => void
}

const fieldByName = (fields: DataSchemaItemFragment[], name: string) => {
  return fields.find((field) => field.fieldName === name)
}

const ErrorsSidepanel = ({
  fields,
  onClose,
  stepData,
  setUnrecognizedEnumName,
  setRowFilter,
}: ErrorsSidepanelProps) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const filteredFields = useUnsafeMemo(() => {
    if (!debouncedSearch) return fields

    return fields.filter((field) => matchText(debouncedSearch, field.label))
  }, [debouncedSearch, fields])

  const filtersCount = {
    error: { count: stepData.rowsWithError?.length || 0 },
    missingName: {
      count: stepData.rowsByFieldErrors?.first_name?.length || 0,
      field: fieldByName(filteredFields, 'first_name'),
      filter: 'missingName',
    },
    missingLastName: {
      count: stepData.rowsByFieldErrors?.last_name?.length || 0,
      field: fieldByName(filteredFields, 'last_name'),
      filter: 'missingLastName',
    },
    invalidEmail: {
      count: stepData.rowsByFieldErrors?.email?.length || 0,
      field: fieldByName(filteredFields, 'email'),
      filter: 'invalidEmail',
    },
    invalidSeniorityCalculationDate: {
      count: stepData.rowsByFieldErrors?.seniority_calculation_date?.length || 0,
      field: fieldByName(filteredFields, 'seniority_calculation_date'),
      filter: 'invalidSeniorityCalculationDate',
    },
    unrecognizedLegalEntity: {
      count: stepData.rowsByFieldErrors?.legal_entity_id?.length || 0,
      field: fieldByName(filteredFields, 'legal_entity_id'),
      filter: 'unrecognizedLegalEntity',
    },
    unrecognizedWorkplace: {
      count: stepData.rowsByFieldErrors?.location_id?.length || 0,
      field: fieldByName(filteredFields, 'location_id'),
      filter: 'unrecognizedWorkplace',
    },
    unrecognizedTeams: {
      count: stepData.rowsByFieldErrors?.team_id?.length || 0,
      field: fieldByName(filteredFields, 'team_id'),
      filter: 'unrecognizedTeams',
    },
    invalidContractStartDate: {
      count: stepData.rowsByFieldErrors?.contract_start_date?.length || 0,
      field: fieldByName(filteredFields, 'contract_start_date'),
      filter: 'invalidContractStartDate',
    },
    unrecognizedJobLevel: {
      count: stepData.rowsByFieldErrors?.job_catalog_level_id?.length || 0,
      field: fieldByName(filteredFields, 'job_catalog_level_id'),
      filter: 'unrecognizedJobLevel',
    },
    invalidPostalCode: {
      count: stepData.rowsByFieldErrors?.postal_code?.length || 0,
      field: fieldByName(filteredFields, 'postal_code'),
      filter: 'invalidPostalCode',
    },
    invalidBirthdayOn: {
      count: stepData.rowsByFieldErrors?.birthday_on?.length || 0,
      field: fieldByName(filteredFields, 'birthday_on'),
      filter: 'invalidBirthdayOn',
    },
    invalidContactName: {
      count: stepData.rowsByFieldErrors?.contact_name?.length || 0,
      field: fieldByName(filteredFields, 'contact_name'),
      filter: 'invalidContactName',
    },
    invalidContactNumber: {
      count: stepData.rowsByFieldErrors?.contact_number?.length || 0,
      field: fieldByName(filteredFields, 'contact_number'),
      filter: 'invalidContactNumber',
    },
    invalidPhoneNumber: {
      count: stepData.rowsByFieldErrors?.phone_number?.length || 0,
      field: fieldByName(filteredFields, 'phone_number'),
      filter: 'invalidPhoneNumber',
    },
    unrecognizedNationality: {
      count: stepData.rowsByFieldErrors?.nationality?.length || 0,
      field: fieldByName(filteredFields, 'nationality'),
      filter: 'unrecognizedNationality',
    },
    unrecognizedCountry: {
      count: stepData.rowsByFieldErrors?.country?.length || 0,
      field: fieldByName(filteredFields, 'country'),
      filter: 'unrecognizedCountry',
    },
    invalidAddressLine1: {
      count: stepData.rowsByFieldErrors?.address_line1?.length || 0,
      field: fieldByName(filteredFields, 'address_line1'),
      filter: 'invalidAddressLine1',
    },
    invalidCity: {
      count: stepData.rowsByFieldErrors?.city?.length || 0,
      field: fieldByName(filteredFields, 'city'),
      filter: 'invalidCity',
    },
    invalidState: {
      count: stepData.rowsByFieldErrors?.state?.length || 0,
      field: fieldByName(filteredFields, 'state'),
      filter: 'invalidState',
    },
    invalidA3InnuvaCode: {
      count: stepData.rowsByFieldErrors?.a3_innuva_code?.length || 0,
      field: fieldByName(filteredFields, 'a3_innuva_code'),
      filter: 'invalidA3InnuvaCode',
    },
    invalidA3NomCode: {
      count: stepData.rowsByFieldErrors?.a3_nom_code?.length || 0,
      field: fieldByName(filteredFields, 'a3_nom_code'),
      filter: 'invalidA3NomCode',
    },
    invalidSalaryAmountInCents: {
      count: stepData.rowsByFieldErrors?.salary_amount_in_cents?.length || 0,
      field: fieldByName(filteredFields, 'salary_amount_in_cents'),
      filter: 'invalidSalaryAmountInCents',
    },
    invalidEndsOnDate: {
      count: stepData.rowsByFieldErrors?.ends_on?.length || 0,
      field: fieldByName(filteredFields, 'ends_on'),
      filter: 'invalidEndsOnDate',
    },
    invalidWorkingHours: {
      count: stepData.rowsByFieldErrors?.working_hours?.length || 0,
      field: fieldByName(filteredFields, 'working_hours'),
      filter: 'invalidWorkingHours',
    },
  }

  const hasMissingInputs =
    (filtersCount.missingName.field && !!filtersCount.missingName.count) ||
    (filtersCount.missingLastName.field && !!filtersCount.missingLastName.count)
  const hasUnrecognizedOptions =
    (filtersCount.unrecognizedLegalEntity.field && !!filtersCount.unrecognizedLegalEntity.count) ||
    (filtersCount.unrecognizedWorkplace.field && !!filtersCount.unrecognizedWorkplace.count) ||
    (filtersCount.unrecognizedTeams.field && !!filtersCount.unrecognizedTeams.count) ||
    (filtersCount.unrecognizedJobLevel.field && !!filtersCount.unrecognizedJobLevel.count) ||
    (filtersCount.unrecognizedNationality.field && !!filtersCount.unrecognizedNationality.count) ||
    (filtersCount.unrecognizedCountry.field && !!filtersCount.unrecognizedCountry.count)
  const hasCriticalErrors = filtersCount.error.count > 0
  const hasInvalidValue =
    (filtersCount.invalidEmail.field && !!filtersCount.invalidEmail.count) ||
    (filtersCount.invalidContractStartDate.field &&
      !!filtersCount.invalidContractStartDate.count) ||
    (filtersCount.invalidSeniorityCalculationDate.field &&
      !!filtersCount.invalidSeniorityCalculationDate.count) ||
    (filtersCount.invalidBirthdayOn.field && !!filtersCount.invalidBirthdayOn.count) ||
    (filtersCount.invalidPostalCode.field && !!filtersCount.invalidPostalCode.count) ||
    (filtersCount.invalidContactName.field && !!filtersCount.invalidContactName.count) ||
    (filtersCount.invalidContactNumber.field && !!filtersCount.invalidContactNumber.count) ||
    (filtersCount.invalidPhoneNumber.field && !!filtersCount.invalidPhoneNumber.count) ||
    (filtersCount.invalidAddressLine1.field && !!filtersCount.invalidAddressLine1.count) ||
    (filtersCount.invalidCity.field && !!filtersCount.invalidCity.count) ||
    (filtersCount.invalidState.field && !!filtersCount.invalidState.count) ||
    (filtersCount.invalidA3InnuvaCode.field && !!filtersCount.invalidA3InnuvaCode.count) ||
    (filtersCount.invalidA3NomCode.field && !!filtersCount.invalidA3NomCode.count) ||
    (filtersCount.invalidSalaryAmountInCents.field &&
      !!filtersCount.invalidSalaryAmountInCents.count) ||
    (filtersCount.invalidEndsOnDate.field && !!filtersCount.invalidEndsOnDate.count) ||
    (filtersCount.invalidWorkingHours.field && !!filtersCount.invalidWorkingHours.count)

  return (
    <Sidepanel
      onClose={onClose}
      open
      title={i18n.t('importer_process.map_columns_step.sidepanel.title')}
      width="wide"
    >
      <Box gap="s32">
        <SearchField
          placeholder={i18n.t('importer_process.map_columns_step.sidepanel.search.placeholder')}
          onSearch={onSearch}
          search={search}
        />
        {hasCriticalErrors && (
          <Callout
            type="critical"
            title={i18n.t('importer_process.edition_step.sidepanel.required_fields_missing')}
            description={i18n.tp('importer_process.edition_step.sidepanel.criticals.description', {
              count: filtersCount.error.count,
            })}
          />
        )}
        {hasMissingInputs && (
          <Box gap="s24">
            <Box gap="s8">
              <Text.Header3>
                {i18n.t('importer_process.edition_step.sidepanel.missing_inputs')}
              </Text.Header3>
              <Text.Normal>
                {i18n.t('importer_process.edition_step.sidepanel.missing_inputs_description')}
              </Text.Normal>
            </Box>
            <FieldList
              fieldsErrors={[filtersCount.missingLastName, filtersCount.missingName]}
              setUnrecognizedEnumName={setUnrecognizedEnumName}
              setRowFilter={setRowFilter}
              onClose={onClose}
            />
          </Box>
        )}
        {hasUnrecognizedOptions && (
          <Box gap="s24">
            <Box gap="s8">
              <Text.Header3>
                {i18n.t('importer_process.edition_step.sidepanel.unrecognizedOptions')}
              </Text.Header3>
              <Text.Normal>
                {i18n.t('importer_process.edition_step.sidepanel.unrecognizedOptionsDescription')}
              </Text.Normal>
            </Box>
            <FieldList
              setUnrecognizedEnumName={setUnrecognizedEnumName}
              fieldsErrors={[
                filtersCount.unrecognizedLegalEntity,
                filtersCount.unrecognizedTeams,
                filtersCount.unrecognizedWorkplace,
                filtersCount.unrecognizedJobLevel,
                filtersCount.unrecognizedNationality,
                filtersCount.unrecognizedCountry,
              ]}
              setRowFilter={setRowFilter}
              onClose={onClose}
            />
          </Box>
        )}
        {hasInvalidValue && (
          <Box gap="s24">
            <Box gap="s8">
              <Text.Header3>
                {i18n.t('importer_process.edition_step.sidepanel.invalidValue')}
              </Text.Header3>
              <Text.Normal>
                {i18n.t('importer_process.edition_step.sidepanel.invalidValueDescription')}
              </Text.Normal>
            </Box>
            <FieldList
              fieldsErrors={[
                filtersCount.invalidEmail,
                filtersCount.invalidSeniorityCalculationDate,
                filtersCount.invalidContractStartDate,
                filtersCount.invalidBirthdayOn,
                filtersCount.invalidPostalCode,
                filtersCount.invalidContactName,
                filtersCount.invalidContactNumber,
                filtersCount.invalidPhoneNumber,
                filtersCount.invalidAddressLine1,
                filtersCount.invalidCity,
                filtersCount.invalidState,
                filtersCount.invalidA3InnuvaCode,
                filtersCount.invalidA3NomCode,
                filtersCount.invalidSalaryAmountInCents,
                filtersCount.invalidEndsOnDate,
                filtersCount.invalidWorkingHours,
              ]}
              setUnrecognizedEnumName={setUnrecognizedEnumName}
              setRowFilter={setRowFilter}
              onClose={onClose}
            />
          </Box>
        )}
      </Box>
    </Sidepanel>
  )
}

export default ErrorsSidepanel
